import { printContract } from "@/lib/printContract";

type Contract = {
  contract_number: string;
  title: string;
  status: string;
  event_date: string;
  event_location: string;
  package_name: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  client_name: string;
  rendered_content: string | null;
  signature_status: string | null;
  client_signature: string | null;
  signed_at: string | null;
};

interface Props {
  contract: Contract;
  tenantName: string;
}

export default function FilesTab({ contract, tenantName }: Props) {
  const handlePrint = () => {
    printContract(
      {
        contract_number:  contract.contract_number,
        title:            contract.title,
        status:           contract.status,
        event_date:       contract.event_date,
        event_location:   contract.event_location,
        package_name:     contract.package_name,
        total_amount:     contract.total_amount,
        deposit_amount:   contract.deposit_amount,
        balance_amount:   contract.balance_amount,
        client_name:      contract.client_name,
        rendered_content: contract.rendered_content,
        signature_status: contract.signature_status,
        client_signature: contract.client_signature,
        signed_at:        contract.signed_at,
      },
      tenantName,
    );
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* Print / PDF */}
      <div className="bg-white rounded-xl border border-slate-200 rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C8A96E]/10 flex-shrink-0">
            <i className="ri-printer-line text-[#C8A96E] text-2xl"></i>
          </div>
          <div>
            <p className="text-[#0f172a] font-semibold text-base mb-1">Print / Save as PDF</p>
            <p className="text-slate-400 text-sm">
              Generate a professional PDF of this contract including all details, contract text, and signature if signed.
            </p>
          </div>
        </div>

        {/* What's included */}
        <div className="bg-slate-50 rounded-lg p-4 mb-5 space-y-2">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Included in PDF</p>
          {[
            { icon: "ri-building-line", label: "Studio name and branding header", active: true },
            { icon: "ri-file-list-3-line", label: "Contract number, title, and event details", active: true },
            { icon: "ri-money-dollar-circle-line", label: "Financial summary (total, deposit, balance)", active: true },
            { icon: "ri-article-line", label: "Contract text / body", active: !!contract.rendered_content },
            { icon: "ri-pen-nib-line", label: "Client signature", active: contract.signature_status === "signed" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${item.active ? "text-emerald-400" : "text-slate-300"}`}>
                <i className={item.active ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"}></i>
              </div>
              <span className={`text-sm ${item.active ? "text-slate-600" : "text-slate-300"}`}>{item.label}</span>
            </div>
          ))}
        </div>

        <button
          onClick={handlePrint}
          className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-printer-line"></i>
          Print / Save PDF
        </button>
        <p className="text-slate-300 text-xs mt-3">
          Your browser&apos;s print dialog will open. Choose &quot;Save as PDF&quot; to download.
        </p>
      </div>

      {/* Contract info summary */}
      <div className="bg-white rounded-xl border border-slate-200 rounded-xl border border-slate-200 p-5">
        <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4">Document Info</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Contract Number</span>
            <span className="text-[#0f172a]/80 text-sm font-medium">{contract.contract_number}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Client</span>
            <span className="text-[#0f172a]/80 text-sm">{contract.client_name}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Status</span>
            <span className={`text-sm font-medium capitalize ${
              contract.status === "signed" || contract.status === "completed" ? "text-emerald-400" :
              contract.status === "active" ? "text-sky-400" :
              contract.status === "cancelled" ? "text-red-400" :
              "text-slate-400"
            }`}>{contract.status}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Signature</span>
            <span className={`text-sm font-medium ${contract.signature_status === "signed" ? "text-emerald-400" : "text-slate-400"}`}>
              {contract.signature_status === "signed" ? "Signed" : "Pending"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Contract Text</span>
            <span className={`text-sm ${contract.rendered_content ? "text-slate-600" : "text-slate-300"}`}>
              {contract.rendered_content ? "Present" : "Not added"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
